import Vue from 'vue';
import { GetterTree, MutationTree } from 'vuex';
import actions from './publisherActions';
import { DataProvider, RootState, typeDevice, typeTraffic } from '@/types';
import {
  InventoryState,
  Inventory,
  Placement,
  EEmptySlotTypes,
  ETimeout,
  ETrigger,
  EPosition,
  IStateInventory,
  IStatePlacements,
  IStatePlacementWidgetParams,
  IWidgetParams,
  TNewData,
  TNewDataWithId,
  IStateContents,
} from '@/types/publisher/inventory';
const DEFAULT_PLACEMENT_TYPE = typeTraffic.BANNER;
import { cloneDeep } from 'lodash';

const widgetParamsInit: IWidgetParams = {
  type: DEFAULT_PLACEMENT_TYPE,
  element: '',
  width: '',
  height: '',
  cols: 3,
  rows: 1,
  useMaxHeight: 0,
  maxHeight: '',
  typeDevice: typeDevice.MOBILE,
  position: EPosition.TOP_RIGHT,
  mobileVerticalMargin: 0,
  desktopVerticalMargin: 10,
  desktopHorizontalMargin: 10,
  openTriggerType: ETrigger.TIMEOUT,
  openTriggerValue: ETimeout.T_5,
  useCloseTrigger: 0,
  closeTriggerType: ETrigger.TIMEOUT,
  closeTriggerValue: ETimeout.T_10,
  errors: {
    element: [],
    width: [],
    height: [],
    cols: [],
    rows: [],
    mobileVerticalMargin: [],
    desktopVerticalMargin: [],
    desktopHorizontalMargin: [],
    maxHeight: [],
    openTriggerValue: [],
    closeTriggerValue: [],
  },
};

const placementInit: Placement = {
  id: '',
  name: '',
  type: DEFAULT_PLACEMENT_TYPE,
  showContentAddButton: true,
  showUnwantedContentAddButton: true,
  emptySlotType: EEmptySlotTypes.CUSTOM,
  contents: [],
  unwantedCategories: [],
  unwantedLinks: [],
  errors: {
    name: [],
  },
};

const inventoryInit: IStateInventory = {
  id: '',
  name: '',
  domain: '',
  status: 'draft',
  languages: [],
  categories: [],
  placements: [],
  errors: {
    name: [],
    domain: [],
  },
};

const state: InventoryState = {
  inventory: null,
  placements: {
    byId: {},
    allIds: [],
  },
  placementContents: {
    byId: {},
    allIds: [],
  },
  placementWidgetParams: {
    byId: {},
    allIds: [],
  },
  inventoryList: {
    paginationData: null,
    data: [] as Array<Inventory>,
  },
  isSavingForm: false,
};

// MUTATIONS
const mutations: MutationTree<InventoryState> = {
  setInventoryListFromResponse(state, data: DataProvider) {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.inventoryList.paginationData = data.paginationData;
    state.inventoryList.data = data.data;
  },
  setInventory(state, inventory: IStateInventory) {
    state.inventory = cloneDeep(inventory);
  },
  setPlacements(state, placements: IStatePlacements) {
    state.placements = cloneDeep(placements);
  },
  setContents(state, contents: IStateContents) {
    state.placementContents = cloneDeep(contents);
  },
  setWidgetParams(state, widgetParams: IStatePlacementWidgetParams) {
    state.placementWidgetParams = cloneDeep(widgetParams);
  },

  addInventory(state, newInventory: IStateInventory): void {
    state.inventory = newInventory;
  },
  addPlacement(state, newPlacement: Placement): void {
    Vue.set(state.placements.byId, newPlacement.id, cloneDeep(newPlacement));
    state.placements.allIds.push(newPlacement.id);
    state.inventory?.placements.push(newPlacement.id);
  },
  addWidgetParams(state, payload) {
    const [placementId, widgetParams] = payload;
    Vue.set(
      state.placementWidgetParams.byId,
      placementId,
      cloneDeep(widgetParams)
    );
    state.placementWidgetParams.allIds.push(placementId);
  },
  addContent(state, payload) {
    const [placementId, content] = payload;

    Vue.set(state.placementContents.byId, content.id, cloneDeep(content));
    state.placementContents.allIds.push(content.id);
    state.placements.byId[placementId].contents.push(content.id);
  },

  updateInventory(state, newData: TNewData): void {
    if (state.inventory) {
      const { propName, value } = newData;
      state.inventory[propName] = value;
    }
  },
  updatePlacement(state, newDataWithId: TNewDataWithId): void {
    if (state.placements) {
      const { id, propName, value } = newDataWithId;
      state.placements.byId[id][propName] = value;
    }
  },
  updateWidgetParams(state, newDataWithId: TNewDataWithId): void {
    const { id, propName, value } = newDataWithId;
    state.placementWidgetParams.byId[id][propName] = value;
  },
  updateContents(state, newContent) {
    state.placementContents.byId[newContent.id] = cloneDeep(newContent);
  },

  deleteInventory(state, id: number): void {
    state.inventoryList.data = state.inventoryList.data.filter((item) => {
      return item.id != id;
    });
  },
  deletePlacement(state, placementId: string): void {
    for (const contentId of state.placements.byId[placementId].contents) {
      Vue.delete(state.placementContents.byId, contentId);
      state.placementContents.allIds = state.placementContents.allIds.filter(
        (id) => id !== contentId
      );
    }

    Vue.delete(state.placements.byId, placementId);
    state.placements.allIds = state.placements.allIds.filter(
      (id) => id !== placementId
    );

    Vue.delete(state.placementWidgetParams.byId, placementId);
    state.placementWidgetParams.allIds =
      state.placementWidgetParams.allIds.filter((id) => id !== placementId);

    if (state.inventory) {
      state.inventory.placements = state.inventory.placements.filter(
        (id) => id !== placementId
      );
    }
  },
  deleteContent(state, payload) {
    const [placementId, contentId] = payload;

    Vue.delete(state.placementContents.byId, contentId);
    state.placementContents.allIds = state.placementContents.allIds.filter(
      (id) => id !== contentId
    );

    state.placements.byId[placementId].contents = state.placements.byId[
      placementId
    ].contents.filter((id) => id !== contentId);
  },
  clearAllState(state) {
    state.inventory = null;
    state.placements = {
      byId: {},
      allIds: [],
    };
    state.placementWidgetParams = {
      byId: {},
      allIds: [],
    };
    state.placementContents = {
      byId: {},
      allIds: [],
    };
    state.isSavingForm = false;
  },

  changeIsSavingForm(state, value: boolean): void {
    state.isSavingForm = value;
  },
};

// GETTERS
const getters: GetterTree<InventoryState, RootState> = {
  getInventoryList(state): Array<Inventory> {
    return state.inventoryList.data.map((i: Inventory) => i);
  },
  getInventory(state): IStateInventory | null {
    return state.inventory;
  },
  getPlacements(state): IStatePlacements {
    return state.placements;
  },
  getPlacementContents(): IStateContents {
    return state.placementContents;
  },
  getPlacementContentsById: (state) => (placementId) => {
    return (
      state.placements.byId[placementId]?.contents.map(
        (contentId) => state.placementContents.byId[contentId]
      ) ?? []
    );
  },
  getPlacementWidgetParams(state): IStatePlacementWidgetParams {
    return state.placementWidgetParams;
  },
  getInventoryInitialState(): IStateInventory {
    return inventoryInit;
  },
  getPlacementInitialState(): Placement {
    return placementInit;
  },
  getWidgetParamsInitialState(): IWidgetParams {
    return widgetParamsInit;
  },
  getIsSavingForm(state): boolean {
    return state.isSavingForm;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
